import { useCallback, useEffect, useState } from "react";
import { Archive, Check, Loader2, Pencil, RotateCcw, Trash2, X } from "lucide-react";
import { api, apiError, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/pages/Leads";
import { Empty } from "@/components/Layout";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const CORE = [
  ["company", "Company / Name"],
  ["contact_person", "Contact person"],
  ["gstin", "GSTIN"],
  ["mobile", "Mobile"],
  ["whatsapp", "WhatsApp"],
];

export function useCustomerRights() {
  const [rights, setRights] = useState({});
  useEffect(() => {
    api.get("/customer-admin/rights").then(({ data }) => setRights(data)).catch(() => setRights({}));
  }, []);
  return rights;
}

/** Edit the customer's core identity fields. Staff edits are held for the Owner's approval. */
export function CustomerEditDialog({ customer, needsApproval, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    CORE.reduce((acc, [k]) => ({ ...acc, [k]: customer[k] || "" }), {}));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.company.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const { data } = await api.put(`/customers/${customer.id}`, { ...customer, ...form });
      if (data?.pending_approval) {
        toast.success("Sent to the Owner for approval — the customer record is unchanged for now");
      } else {
        toast.success("Customer updated");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="customer-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {customer.company}</DialogTitle>
          <DialogDescription>
            {needsApproval
              ? "Your changes to these details will wait for the Owner's approval."
              : "Changes are saved immediately."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {CORE.map(([key, label]) => (
            <Field key={key} label={label}>
              <Input data-testid={`customer-edit-${key}`} value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            </Field>
          ))}
        </div>
        <DialogFooter>
          <Button data-testid="customer-edit-save" disabled={saving} onClick={save}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {needsApproval ? "Send for approval" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Soft delete / permanent purge with a written reason kept as proof. */
export function CustomerDeleteDialog({ customer, mode, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const purge = mode === "purge";

  const run = async () => {
    if (reason.trim().length < 5) return toast.error("Please write why — it is kept as proof");
    setBusy(true);
    try {
      if (purge) {
        await api.delete(
          `/customer-admin/${customer.id}/purge?reason=${encodeURIComponent(reason.trim())}`);
        toast.success(`${customer.company} permanently removed`);
      } else {
        await api.post(`/customer-admin/${customer.id}/archive`, { reason: reason.trim() });
        toast.success(`${customer.company} archived — history preserved`);
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="customer-delete-dialog">
        <DialogHeader>
          <DialogTitle>{purge ? "Permanently delete" : "Archive"} {customer.company}</DialogTitle>
          <DialogDescription>
            {purge
              ? "Only dummy or test customers with no quotation, invoice or payment history can be removed permanently."
              : "The customer disappears from new quotations and invoices. Every past record stays linked and you can restore any time."}
          </DialogDescription>
        </DialogHeader>
        <Field label="Reason (kept as proof)">
          <Input data-testid="customer-delete-reason" value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={purge ? "Dummy entry created by mistake" : "Customer no longer active"} />
        </Field>
        <DialogFooter>
          <Button variant={purge ? "destructive" : "default"} disabled={busy} onClick={run}
            data-testid="customer-delete-confirm">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {purge ? "Delete permanently" : "Archive customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerRowActions({ customer, rights, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState("");
  if (!rights.can_delete_customers && !rights.is_root && rights.can_approve_customer_edits === undefined)
    return null;
  const pending = customer.edit_status === "pending_approval";
  return (
    <div className="flex items-center justify-end gap-1">
      {pending && (
        <Badge variant="outline" data-testid={`customer-pending-badge-${customer.id}`}>
          Edit pending approval
        </Badge>
      )}
      <Button size="icon" variant="ghost" title="Edit core details" disabled={pending}
        data-testid={`customer-edit-button-${customer.id}`} onClick={() => setEdit(true)}>
        <Pencil className="h-4 w-4" />
      </Button>
      {rights.can_delete_customers && (
        <Button size="icon" variant="ghost" title="Archive (soft delete)"
          data-testid={`customer-archive-button-${customer.id}`} onClick={() => setDel("archive")}>
          <Archive className="h-4 w-4" />
        </Button>
      )}
      {rights.is_root && rights.can_delete_customers && (
        <Button size="icon" variant="ghost" title="Permanently delete a dummy entry"
          data-testid={`customer-purge-button-${customer.id}`} onClick={() => setDel("purge")}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
      {edit && (
        <CustomerEditDialog customer={customer} needsApproval={rights.edits_need_approval}
          onClose={() => setEdit(false)} onSaved={onChanged} />
      )}
      {del && (
        <CustomerDeleteDialog customer={customer} mode={del} onClose={() => setDel("")}
          onDone={onChanged} />
      )}
    </div>
  );
}

export function PendingCustomerEdits({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-admin/pending-edits");
      setRows(data.pending);
      setCanApprove(data.can_approve);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (cid, action) => {
    setBusy(cid);
    try {
      await api.post(`/customer-admin/pending-edits/${cid}/${action}`,
        action === "reject" ? { reason: "Not approved" } : {});
      toast.success(action === "approve" ? "Edit approved and applied" : "Edit rejected");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading)
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  if (rows.length === 0)
    return <Empty title="Nothing waiting" hint="Staff edits to customer details will appear here." />;

  return (
    <div className="grid gap-3" data-testid="pending-customer-edits">
      {rows.map((r) => (
        <div key={r.customer_id} className="surface p-4" data-testid={`pending-edit-${r.customer_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-sm font-medium">{r.company}</div>
              <div className="text-xs text-muted-foreground">
                {r.code} • submitted by {r.submitted_by || "staff"} • {shortDate(r.submitted_at)}
              </div>
            </div>
            {canApprove && (
              <div className="flex gap-2">
                <Button size="sm" disabled={busy === r.customer_id}
                  data-testid={`approve-edit-${r.customer_id}`}
                  onClick={() => decide(r.customer_id, "approve")}>
                  <Check className="mr-1 h-4 w-4" /> Approve Edit
                </Button>
                <Button size="sm" variant="outline" disabled={busy === r.customer_id}
                  data-testid={`reject-edit-${r.customer_id}`}
                  onClick={() => decide(r.customer_id, "reject")}>
                  <X className="mr-1 h-4 w-4" /> Reject Edit
                </Button>
              </div>
            )}
          </div>
          <Table className="mt-3">
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Current value</TableHead>
                <TableHead>Proposed value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.changes.map((c) => (
                <TableRow key={c.field}>
                  <TableCell className="text-sm">{c.label}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.current || "-"}</TableCell>
                  <TableCell className="text-sm font-medium">{c.proposed || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

export function ArchivedCustomers({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-admin/archived");
      setRows(data.customers);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/customer-admin/${c.id}/restore`);
      toast.success(`${c.company} restored`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading)
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  if (rows.length === 0)
    return <Empty title="No archived customers" hint="Soft-deleted customers appear here." />;

  return (
    <div className="surface overflow-x-auto" data-testid="archived-customers-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Archived by</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>History kept</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.id} data-testid={`archived-customer-${c.id}`}>
              <TableCell className="text-sm font-medium">
                {c.company}
                <div className="text-xs text-muted-foreground">{c.code}</div>
              </TableCell>
              <TableCell className="text-sm">
                {c.deleted_by_name || "-"}
                <div className="text-xs text-muted-foreground">{shortDate(c.deleted_at)}</div>
              </TableCell>
              <TableCell className="text-sm">{c.delete_reason || "-"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {Object.entries(c.linked || {}).map(([k, v]) => `${v} ${k}`).join(", ") || "none"}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" disabled={busy === c.id}
                  data-testid={`restore-customer-${c.id}`} onClick={() => restore(c)}>
                  <RotateCcw className="mr-1 h-4 w-4" /> Restore
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
